import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, CATEGORIES } from "../lib/api";
import { useAuth, formatErr } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, FileText, Send, Trash2 } from "lucide-react";

const STATUS_STYLES = {
  bozza: "bg-slate-100 text-slate-800",
  inviato: "bg-emerald-100 text-emerald-900",
};

const fmtEur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminQuoteDrafts() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const isStaff = user?.role === "personale" || user?.role === "admin";

  useEffect(() => { if (!loading && !isStaff) navigate("/login"); }, [user, loading, navigate, isStaff]);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/quote-drafts${filter !== "all" ? `?status=${filter}` : ""}`);
      setDrafts(r.data);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  useEffect(() => { if (isStaff) load(); /* eslint-disable-next-line */ }, [filter, user]);

  const del = async (d) => {
    if (!window.confirm(`Eliminare il preventivo per ${d.client_name}?`)) return;
    try { await api.delete(`/admin/quote-drafts/${d.draft_id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (!isStaff) return <div className="min-h-screen"><Navbar/></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mt-6 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Staff</div>
            <h1 className="heading text-3xl md:text-4xl font-extrabold text-slate-900 flex items-center gap-2">
              <FileText className="w-7 h-7"/> Preventivi Staff
            </h1>
            <p className="text-sm text-slate-600 mt-1">Crea preventivi assistiti dall'IA, esporta il PDF e invialo al cliente.</p>
          </div>
          <div className="flex gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-48" data-testid="drafts-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="bozza">Bozze</SelectItem>
                <SelectItem value="inviato">Inviati</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setCreateOpen(true)} className="bg-blue-800 hover:bg-blue-900" data-testid="draft-new-btn">
              <Plus className="w-4 h-4 mr-2"/> Nuovo preventivo
            </Button>
          </div>
        </div>

        {busy && <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-800 mx-auto"/></div>}
        {!busy && drafts.length === 0 && (
          <Card className="p-12 text-center border-dashed">
            <p className="text-slate-600">Nessun preventivo. Vuoi crearne uno con l'IA?</p>
            <Button onClick={() => setCreateOpen(true)} className="mt-4 bg-blue-800">Crea il primo</Button>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {drafts.map(d => (
            <Card key={d.draft_id} className="p-5 hover:shadow-md transition-shadow" data-testid={`draft-${d.draft_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[d.status]}`}>{d.status}</span>
                    <span className="text-xs text-slate-500 font-mono">{d.draft_id}</span>
                  </div>
                  <div className="heading font-bold text-lg text-slate-900 truncate">{d.client_name}</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    {d.category || "—"} · {d.city || "—"}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {(d.items || []).length} voci · Totale <b className="text-slate-800">{fmtEur(d.totals?.total)}</b>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Link to={`/admin/preventivi-staff/${d.draft_id}`}>
                    <Button size="sm" variant="outline" data-testid={`draft-open-${d.draft_id}`}>Apri</Button>
                  </Link>
                  <Button size="sm" variant="outline" onClick={() => del(d)} className="text-red-600" data-testid={`draft-del-${d.draft_id}`}>
                    <Trash2 className="w-3.5 h-3.5"/>
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <NewDraftDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(id) => { setCreateOpen(false); navigate(`/admin/preventivi-staff/${id}`); }}/>
    </div>
  );
}

function NewDraftDialog({ open, onOpenChange, onCreated }) {
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [category, setCategory] = useState("");
  const [city, setCity] = useState("");
  const [description, setDescription] = useState("");
  const [quoteId, setQuoteId] = useState("");
  const [openQuotes, setOpenQuotes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setClientName(""); setClientEmail(""); setClientPhone("");
    setCategory(""); setCity(""); setDescription(""); setQuoteId("");
    api.get("/admin/quotes").then(r => setOpenQuotes(r.data.slice(0, 200))).catch(()=>{});
  }, [open]);

  const linkQuote = (qid) => {
    setQuoteId(qid);
    const q = openQuotes.find(x => x.quote_id === qid);
    if (q) {
      setClientName(q.name || "");
      setClientEmail(q.email || "");
      setClientPhone(q.phone || "");
      setCategory(q.category || "");
      setCity(q.city || "");
      setDescription(q.summary || "");
    }
  };

  const create = async () => {
    if (!clientName.trim()) { toast.error("Inserisci il nome cliente"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/quote-drafts", {
        quote_id: quoteId || null,
        client_name: clientName, client_email: clientEmail || null, client_phone: clientPhone || null,
        category: category || null, city: city || null, description: description || null,
        items: [], vat_rate: 22, discount: 0,
      });
      toast.success("Preventivo creato");
      onCreated(r.data.draft_id);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="heading">Nuovo preventivo staff</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Collega a richiesta esistente (opzionale)</label>
            <Select value={quoteId || "none"} onValueChange={v => v === "none" ? setQuoteId("") : linkQuote(v)}>
              <SelectTrigger data-testid="draft-quote-link"><SelectValue placeholder="Nessuna"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nessuna —</SelectItem>
                {openQuotes.map(q => (
                  <SelectItem key={q.quote_id} value={q.quote_id}>{q.quote_id} · {q.name} · {q.category}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Nome cliente *</label>
            <Input value={clientName} onChange={e=>setClientName(e.target.value)} data-testid="draft-client-name"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Email</label>
              <Input value={clientEmail} onChange={e=>setClientEmail(e.target.value)} data-testid="draft-client-email"/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Telefono</label>
              <Input value={clientPhone} onChange={e=>setClientPhone(e.target.value)} data-testid="draft-client-phone"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Categoria</label>
              <Select value={category || "none"} onValueChange={v => setCategory(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="draft-category"><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Città</label>
              <Input value={city} onChange={e=>setCity(e.target.value)} data-testid="draft-city"/>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Descrizione lavoro</label>
            <textarea className="w-full min-h-[100px] rounded-md border border-slate-300 p-2 text-sm" value={description} onChange={e=>setDescription(e.target.value)} data-testid="draft-description"/>
          </div>
          <Button onClick={create} disabled={busy || !clientName.trim()} className="w-full bg-blue-800 hover:bg-blue-900" data-testid="draft-create-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Crea preventivo"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
